/* eslint-disable ember/no-classic-components, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { isArray } from '@ember/array';
import EmberObject, { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import {
  getCurrentMilestone,
  subjectProgressions,
  generateStudentProgressData,
} from '@linc-technologies/linced-common/utils/progressions';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import Options from '@linc-technologies/linced-common/enums/options';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';

export default class SubjectSummary extends Component {
  // Services
  @service
  colours;

  @service
  dates;

  @service
  heroApi;

  @service
  options;

  @service
  session;

  @alias('subject.isGradable')
  showGrader;

  @optionAlias(Options.CHARTS_SHOW_ALL_COLOURS)
  showAllColours;

  @optionAlias(Options.EXPECTATIONS_PROGRESS_CHART_KEY)
  showProgressChartKey;

  @optionAlias(Options.EXPECTATIONS_SHOW_ACHIEVEMENT_STATEMENTS)
  showAchievementStatements;

  @optionAlias(Options.EXPECTATIONS_SHOW_CURRENT_EXPECTATION)
  showCurrentExpectation;

  @optionAlias(Options.GOALS_VISIBLE)
  showGoals;

  @optionAlias('parents:goals:showEvidence')
  showEvidence;

  @optionAlias(Options.PROGRESS_REPORTING_ENABLED)
  progressionsEnabled;

  @computed('session.activeSchool')
  get progressChartColors() {
    return this.colours.charts(this.session.school);
  }

  @alias('subject.milestones')
  sortedMilestones;

  @computed(
    'sortedMilestones.[]',
    'person.{weeksAtSchool,monthsAtSchool,currentYearLevel}',
    'endTime',
  )
  get currentMilestone() {
    let asAtDate;
    if (this.endTime) {
      asAtDate = moment(this.endTime * 1000).format('YYYY-MM-DD');
    }
    return getCurrentMilestone(
      this.sortedMilestones,
      this.person,
      this.dates.rolloverMonthDate,
      asAtDate,
    );
  }

  @computed('currentMilestone', 'sortedMilestones.[]')
  get previousMilestone() {
    let currentIndex = this.sortedMilestones.indexOf(this.currentMilestone);
    return this.sortedMilestones[currentIndex - 1];
  }

  @computed('subject.showRadialDials', 'isParentView')
  get showProgressDials() {
    if (this.subject.showRadialDials) {
      // In parent view only actually display if the student is working on or has completed any goals
      return (
        !this.isParentView ||
        !isEmpty(this.developingStates) ||
        !isEmpty(this.secureStates)
      );
    }
    return false;
  }

  @computed('categories.[]', 'subject.id')
  get filteredCategories() {
    if (!isArray(this.categories)) {
      return [];
    }
    let subjectId = this.subject.id;
    return this.categories.filter(
      (category) => category.subject_id === subjectId,
    );
  }

  @computed('showProgressDials', 'subject.trackable', 'progressions')
  get showProgressSliders() {
    return (
      !this.showProgressDials && this.subject.trackable && this.progressions
    );
  }

  expectations = [];
  progressionRecords = [];

  @computed(
    'progressionsEnabled',
    'subject.{id,trackable}',
    'person.id',
    'levels.[]',
    'expectations.[]',
    'progressionRecords.[]',
    'sortedMilestones.[]',
  )
  get progressions() {
    if (!this.progressionsEnabled || !this.subject.trackable) {
      return;
    }

    let milestones = this.sortedMilestones;
    let levels = this.levels;
    let expectations = this.expectations;
    if (isEmpty(milestones) || isEmpty(levels) || isEmpty(expectations)) {
      // We need Milestones, Levels and Expectations to display anything.
      return;
    }

    // Generate progression data for this Subject
    let progressions = this.progressionRecords;
    if (!isEmpty(progressions)) {
      return generateStudentProgressData(
        expectations,
        milestones,
        levels,
        progressions,
        this.showAllColours,
      );
    }
  }

  @optionAlias(Options.EXPECTATIONS_INCLUDE_CURRICULUM_LEVEL)
  showCurriculumLevel;

  @tracked learnerSummary = null;

  @computed(
    'subject.{id,trackable}',
    'person.id',
    'levels.[]',
    'expectations.[]',
    'progressionRecords.[]',
    'displayData',
    'learnerSummary',
  )
  get progressDialData() {
    let levels = this.levels;
    let subjectData;
    if (this.subject.trackable) {
      let { expectation, progression } = this.displayData;
      subjectData = subjectProgressions(
        this.subject,
        levels,
        expectation,
        progression,
      );
      if (!isEmpty(subjectData)) {
        subjectData.set('summary', this.learnerSummary);
      }
    } else {
      subjectData = EmberObject.create({});
      subjectData.set('summary', this.learnerSummary);
    }

    return subjectData;
  }

  // statementData needs generating if we're not also displaying progress dials.
  @computed(
    'subject.{id,trackable}',
    'showProgressDials',
    'levels.[]',
    'expectations.[]',
    'progressionRecords.[]',
    'displayData',
  )
  get statementData() {
    if (!this.subject.trackable) {
      return;
    }
    if (this.showProgressDials) {
      return this.progressDialData;
    }

    if (isEmpty(this.levels) || isEmpty(this.expectations)) {
      // We need Levels and Expectations to display anything.
      return;
    }

    let { expectation, progression } = this.displayData;
    return subjectProgressions(
      this.subject,
      this.levels,
      expectation,
      progression,
    );
  }

  // statementMilestone returns the milestone to display in the achievement/expectation statement.
  @alias('displayData.milestone')
  statementMilestone;

  @tracked
  recentGoals;

  @tracked
  recentGoalsSubject;

  @tracked
  showRecentGoalsDialog;

  // displayData returns the expectation, progression and milestone to be displayed in statements etc.
  @computed(
    'subject.id',
    'person.id',
    'levels.[]',
    'expectations.[]',
    'progressionRecords.[]',
  )
  get displayData() {
    let milestone = this.currentMilestone;
    let expectation = findBy(this.expectations, 'milestone.id', milestone.id);
    let progression = findBy(
      this.progressionRecords,
      'milestone.id',
      milestone.id,
    );

    // Use previous milestone if there's no data for the current one (but is for the previous).
    if (isEmpty(progression) && !isEmpty(this.previousMilestone)) {
      let prevMilestone = this.previousMilestone;
      let prevExpectation = findBy(
        this.expectations,
        'milestone.id',
        prevMilestone.id,
      );
      let prevProgression = findBy(
        this.progressionRecords,
        'milestone.id',
        prevMilestone.id,
      );
      if (!isEmpty(prevProgression)) {
        milestone = prevMilestone;
        expectation = prevExpectation;
        progression = prevProgression;
      }
    }

    return { expectation, progression, milestone };
  }

  @computed('isParentView')
  get numRecentGoals() {
    return this.isParentView
      ? this.options.value('parents:goals:numRecentGoals') || 5
      : 5;
  }

  @computed('isParentView')
  get numCurrentGoals() {
    return this.isParentView
      ? this.options.value('parents:goals:numCurrentGoals') || 5
      : undefined;
  }

  @computed('isParentView')
  get showRecentGoals() {
    return this.isParentView
      ? this.options.value('parents:goals:showSecure')
      : true;
  }

  @computed('isParentView')
  get showCurrentGoals() {
    return this.isParentView
      ? this.options.value('parents:goals:showCurrent')
      : true;
  }

  @computed('isParentView')
  get showGoalProgress() {
    return this.isParentView
      ? this.options.value('parents:goals:showGoalProgress')
      : true;
  }

  @action
  handleShowRecentGoals(subject, goals) {
    this.recentGoalsSubject = subject;
    this.recentGoals = goals;
    this.showRecentGoalsDialog = true;
  }

  @action
  hideRecentGoals() {
    this.showRecentGoalsDialog = false;
  }
}
